import type { LitSquadInventoryEntry, LitSquadInventoryItemDetail } from '@/lib/api';

/**
 * Case-insensitive presence check for a key on an item-metadata object.
 * Legacy LitSquad imports use PascalCase keys (`Eyes`, `Group`) while
 * newer crafted items use lowerCamelCase (`eyes`, `group`), so name
 * matches must be case-insensitive.
 */
function hasMetadataField(meta: Record<string, unknown> | null | undefined, name: string): boolean {
  if (!meta) return false;
  const lower = name.toLowerCase();
  for (const key of Object.keys(meta)) {
    if (key.toLowerCase() === lower) return true;
  }
  return false;
}

/**
 * Returns `true` if a LitSquad inventory entry represents a playable
 * character (an avatar base) and `false` if it represents a crafted or
 * legacy-imported accessory.
 *
 * Legacy import buckets BOTH characters and accessories under the same
 * 17 `litdef_species_*` definition ids, so the def-id alone can't
 * discriminate. The shape of the per-item `metadata` is the source of
 * truth:
 *
 *   - Characters carry the rendering attributes `Eyes`, `Ears`, `Face`,
 *     `Tail`, etc., and never have a `Group`.
 *   - Accessories carry a `Group` (e.g. `HeadItems`, `Shirts`,
 *     `HoldingLeft`) and never have body-part attributes.
 *
 * If metadata is missing or ambiguous (e.g. admin-minted items without a
 * legacy stats payload), the display name pattern is used as a fallback:
 *
 *   - `"LitSquad <Species> <number>"`   → character
 *   - `"LitSquad <Species> <words...>"` → accessory
 *
 * The default when no signal is present is "character" — this matches
 * how plain `litdef_species_*` rows are seeded by the migration.
 */
export function isLitSquadCharacter(
  entry: Pick<LitSquadInventoryEntry, 'itemMetadata' | 'displayName'>,
): boolean {
  const meta = entry.itemMetadata;
  if (hasMetadataField(meta, 'eyes')) return true;
  if (hasMetadataField(meta, 'group')) return false;

  const name = entry.displayName?.trim();
  if (name) {
    // Characters look like "LitSquad Dog 100018"; accessories look like
    // "LitSquad Dog Golf Club". After the species token, characters have
    // a numeric instance suffix; accessories have one or more words.
    const match = name.match(/^LitSquad\s+\S+\s+(.+)$/i);
    if (match) {
      return /^\d+$/.test(match[1].trim());
    }
    // "LitSquad Dog" with no suffix — admin-minted character template.
    if (/^LitSquad\s+\S+$/i.test(name)) return true;
  }

  return true;
}

/**
 * Validates that a URI is a safe HTTP(S) URL suitable for use as an image src.
 */
export function isSafeImageUri(uri: string | null | undefined): boolean {
  if (!uri) return false;
  try {
    const url = new URL(typeof uri === 'string' ? uri.trim() : uri);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Preferred display name for a LitSquad inventory row. Falls back to the
 * species-level definition name when the item has no per-item override
 * (e.g. admin-minted items without a legacy asset_name).
 */
export function resolveLitSquadDisplayName(
  entry: Pick<LitSquadInventoryEntry, 'displayName' | 'speciesName'>,
): string {
  return entry.displayName && entry.displayName.trim().length > 0
    ? entry.displayName
    : entry.speciesName;
}

/**
 * Preferred thumbnail for a LitSquad inventory row — the item-level override
 * (legacy icon rehosted under arche_dfe_icons/LitSquad/) takes precedence
 * over the species-level definition default.
 */
export function resolveLitSquadThumbnail(
  entry: Pick<LitSquadInventoryEntry, 'thumbnailUri' | 'definitionThumbnailUri'>,
): string | null {
  return entry.thumbnailUri ?? entry.definitionThumbnailUri ?? null;
}

export function resolveLitSquadItemDisplayName(
  item: Pick<LitSquadInventoryItemDetail, 'displayName'>,
  speciesName: string | null,
): string {
  if (item.displayName && item.displayName.trim().length > 0) return item.displayName;
  return speciesName ?? 'LitSquad NFT';
}
